import {
  buildMarkdownSourceMap,
  buildTextInlineDisplaySpans,
  buildTextInlineDisplaySpansForRange,
  isHttpUrl,
  markdownCalloutHasTitle,
  markdownCalloutTitleDisplayText,
  markdownOrderedSourceNumber,
  markdownRawLineText,
  type MarkdownBlock,
  type MarkdownCallout,
  type MarkdownCodeBlock,
  type MarkdownSourceMap,
  type MarkdownTable,
  type MarkdownTableRow,
  type TextInlineDisplaySpan,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";

// 本文（Markdown）を、他アプリへ貼り付けるための HTML 断片へ変換する純粋関数群。
//
// 方針:
// - 独自パーサを書かず、画面描画と同じ解析資産（buildMarkdownSourceMap / buildTextInlineDisplaySpans）
//   を通す。走査は「ブロック層 → 行 → インライン span」の 3 段で、表・コードブロック・callout は
//   行種別ではなくブロック層（sourceMap.blocks）で判定する。
// - 目的は「貼り付け先で構造が保たれること」であり、画面と同じ見た目の再現ではない。
//   色・文字サイズ・行間・CSS クラスは 1 つも出力しない（貼り付け先のスタイル体系と衝突するため）。
// - 本モジュールは DOM・React へ依存しない。クリップボード書き込み側（canvas-clipboard.ts →
//   canvas-object-clipboard-text.ts → 本モジュール）の依存の向きは一方向で、循環しない。

/** `<a href>` へ入れてよい URL の最大長（domain の isHttpUrl と同じ上限）。 */
const LINK_URL_MAX_LENGTH = 8192;

/** コード範囲の識別子を持たないが装飾だけがコードである span をまとめるための番号。 */
const DECORATION_ONLY_CODE_GROUP = -1;

/**
 * HTML の本文・属性値へ入れる文字列を textContent 相当へエスケープする。
 * クリップボード用 HTML を組み立てる 2 つのモジュール（本モジュールと
 * canvas-object-clipboard-text.ts）が同じ規則を共有するため、葉側の本モジュールが正本を持つ。
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/gu, "&amp;")
    .replace(/</gu, "&lt;")
    .replace(/>/gu, "&gt;")
    .replace(/"/gu, "&quot;")
    .replace(/'/gu, "&#39;");
}

/**
 * Markdown 本文を、クリップボード（text/html）用の HTML 断片へ変換する。
 * `inlineStyles` はテキスト図形の部分書式（run）で、太字だけを装飾要素へ反映する。
 * 解析が例外を投げた場合は、本文を行ごとの `<p>` へ落とした退避結果を返し、呼び出し側へ
 * 例外を伝播しない（クリップボードのコピー全体を失敗させないため）。
 */
export function markdownToClipboardHtml(
  markdown: string,
  inlineStyles?: readonly TextInlineStyleRange[] | undefined
): string {
  if (markdown.length === 0) return "";
  try {
    const map = buildMarkdownSourceMap(markdown);
    return renderBlocks(map, map.blocks, inlineStyles);
  } catch {
    return renderPlainFallback(markdown);
  }
}

/** 解析できなかった本文の退避表現（記法を解釈せず、行ごとの段落にする）。 */
function renderPlainFallback(markdown: string): string {
  return markdown
    .split(/\r?\n/u)
    .filter((line) => line.length > 0)
    // 退避表現。補間値は行テキストを escapeHtml() したものだけ。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
}

/**
 * ブロック列を HTML へ変換する。callout の本文も同じ関数を通すため、リスト・引用の
 * 開閉状態は呼び出しごとに閉じた状態で持つ（外側のリストが内側へ漏れない）。
 */
function renderBlocks(
  map: MarkdownSourceMap,
  blocks: readonly MarkdownBlock[],
  runs: readonly TextInlineStyleRange[] | undefined
): string {
  const out: string[] = [];
  // 開いているリストのタグ（配列長 = ネストの深さ）。各深さにちょうど 1 つの <li> が開いている。
  const lists: ("ul" | "ol")[] = [];
  // 連続する引用行は 1 つの <blockquote> へまとめる。
  let quoted: string[] = [];

  const flushQuote = (): void => {
    if (quoted.length === 0) return;
    // 連結するのは、この関数群が組み立て済みの断片だけ。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    out.push(`<blockquote>${quoted.join("")}</blockquote>`);
    quoted = [];
  };
  const closeLists = (): void => {
    // 補間値は開いているリスト種別（"ul" / "ol"）の固定値だけ。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    while (lists.length > 0) out.push(`</li></${lists.pop() ?? "ul"}>`);
  };
  const closeOpenBlocks = (): void => {
    flushQuote();
    closeLists();
  };

  /** 指定した深さへリストを開き直し、新しい `<li>` を開く。 */
  const openListItem = (tag: "ul" | "ol", level: number, startNumber?: number | undefined): void => {
    // 階層を飛ばした字下げでも <ul> の直下に <ul> を作らないよう、1 段ずつしか深くしない。
    const depth = Math.min(level, lists.length);
    // 補間値は開いているリスト種別（"ul" / "ol"）の固定値だけ。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    while (lists.length > depth + 1) out.push(`</li></${lists.pop() ?? "ul"}>`);
    if (lists.length === depth + 1) {
      if (lists[depth] === tag) {
        out.push("</li>");
      } else {
        // 補間値は開いているリスト種別（"ul" / "ol"）の固定値だけ。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        out.push(`</li></${lists.pop() ?? "ul"}>`);
      }
    }
    if (lists.length < depth + 1) {
      const needsStart = tag === "ol" && startNumber !== undefined && startNumber !== 1;
      // 補間値は開始番号（数値）とリスト種別の固定値だけ。
      // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
      out.push(needsStart ? `<ol start="${String(startNumber)}">` : `<${tag}>`);
      lists.push(tag);
    }
    out.push("<li>");
  };

  for (const block of blocks) {
    if (block.kind === "table") {
      closeOpenBlocks();
      out.push(renderTable(map, block.table, runs));
      continue;
    }
    if (block.kind === "code") {
      closeOpenBlocks();
      out.push(renderCodeBlock(map, block.code));
      continue;
    }
    if (block.kind === "callout") {
      closeOpenBlocks();
      out.push(renderCallout(map, block.callout, runs));
      continue;
    }
    const line = map.lines[block.lineIndex];
    if (line === undefined) continue;
    switch (line.kind) {
      case "blank":
        closeOpenBlocks();
        break;
      case "rule":
        closeOpenBlocks();
        out.push("<hr>");
        break;
      case "heading": {
        closeOpenBlocks();
        const inner = renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text }));
        if (inner.length > 0) {
          const level = Math.min(Math.max(line.level, 1), 6);
          // level は 1〜6 に丸めた数値、inner は renderSpans() が escape 済み span から作った断片。
          // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
          out.push(`<h${String(level)}>${inner}</h${String(level)}>`);
        }
        break;
      }
      case "quote":
        closeLists();
        quoted.push(
          // 補間値は renderSpans() が escape 済み span から作った断片。
          // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
          `<p>${renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text }))}</p>`
        );
        break;
      case "bullet":
        flushQuote();
        openListItem("ul", line.level);
        out.push(renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text })));
        break;
      case "task":
        flushQuote();
        openListItem("ul", line.level);
        out.push(
          line.checked
            ? '<input type="checkbox" disabled checked>'
            : '<input type="checkbox" disabled>'
        );
        out.push(renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text })));
        break;
      case "ordered":
        flushQuote();
        openListItem("ol", line.level, markdownOrderedSourceNumber(line));
        out.push(renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text })));
        break;
      default: {
        closeOpenBlocks();
        const inner = renderSpans(buildTextInlineDisplaySpans({ line, runs, text: map.text }));
        // 補間値は renderSpans() が escape 済み span から作った断片。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        if (inner.length > 0) out.push(`<p>${inner}</p>`);
        break;
      }
    }
  }
  closeOpenBlocks();
  return out.join("");
}

/** GFM の表を `<table><thead><tbody>` へ変換する。 */
function renderTable(
  map: MarkdownSourceMap,
  table: MarkdownTable,
  runs: readonly TextInlineStyleRange[] | undefined
): string {
  const header = renderTableRow(map, table.header, "th", runs);
  const body = table.body.map((row) => renderTableRow(map, row, "td", runs)).join("");
  // 補間値は renderTableRow() が作った断片だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<table><thead>${header}</thead><tbody>${body}</tbody></table>`;
}

function renderTableRow(
  map: MarkdownSourceMap,
  row: MarkdownTableRow,
  cellTag: "th" | "td",
  runs: readonly TextInlineStyleRange[] | undefined
): string {
  const cells = row.cells
    .map((cell) => {
      const spans = buildTextInlineDisplaySpansForRange({
        collapsePipeEscape: true,
        end: cell.end,
        runs,
        start: cell.start,
        text: map.text,
      });
      return `<${cellTag}>${renderSpans(spans)}</${cellTag}>`;
    })
    .join("");
  // 補間値は、この関数が直前で組み立てたセル断片だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<tr>${cells}</tr>`;
}

/** コードブロックは中身を一切解釈せず、生テキストのまま `<pre><code>` へ入れる。 */
function renderCodeBlock(map: MarkdownSourceMap, code: MarkdownCodeBlock): string {
  const lines = code.contentLineIndexes.map((lineIndex) => markdownRawLineText(map, lineIndex));
  // 補間値はコードブロックの生テキストを escapeHtml() したものだけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<pre><code>${escapeHtml(lines.join("\n"))}</code></pre>`;
}

/** callout は `<blockquote>` にし、タイトルがあれば先頭へ `<strong>` として置く。 */
function renderCallout(
  map: MarkdownSourceMap,
  callout: MarkdownCallout,
  runs: readonly TextInlineStyleRange[] | undefined
): string {
  const parts: string[] = [];
  if (markdownCalloutHasTitle(map.text, callout)) {
    const title = escapeHtml(markdownCalloutTitleDisplayText(map.text, callout));
    // 補間値は escapeHtml() 済みの callout タイトルだけ。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    parts.push(`<p><strong>${title}</strong></p>`);
  }
  parts.push(renderBlocks(map, callout.body, runs));
  // 補間値は、この関数群が組み立て済みの断片だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<blockquote>${parts.join("")}</blockquote>`;
}

/**
 * 表示 span 列を HTML へ変換する。
 * 同じリンク先が続く範囲を 1 つの `<a>` でまとめ、その内側でコード範囲・装飾要素を作る
 * （`<a><strong>…</strong></a>` の順。`<a>` の中へ装飾を入れる）。
 */
function renderSpans(spans: readonly TextInlineDisplaySpan[]): string {
  let html = "";
  let index = 0;
  while (index < spans.length) {
    const href = linkHref(spans[index]?.url);
    let end = index + 1;
    while (end < spans.length && linkHref(spans[end]?.url) === href) end += 1;
    const inner = renderCodeGroups(spans.slice(index, end));
    // href は linkHref() が許可したスキームだけで、さらに escapeHtml() 済み。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    html += href !== null && inner.length > 0 ? `<a href="${escapeHtml(href)}">${inner}</a>` : inner;
    index = end;
  }
  return html;
}

/** 同じコード範囲が続く span をまとめて 1 つの `<code>` で囲む。 */
function renderCodeGroups(spans: readonly TextInlineDisplaySpan[]): string {
  let html = "";
  let index = 0;
  while (index < spans.length) {
    const span = spans[index];
    if (span === undefined) break;
    const group = codeGroupOf(span);
    if (group === null) {
      html += renderDecoratedSpan(span);
      index += 1;
      continue;
    }
    let end = index + 1;
    while (end < spans.length) {
      const next = spans[end];
      if (next === undefined || codeGroupOf(next) !== group) break;
      end += 1;
    }
    const inner = spans
      .slice(index, end)
      .map((member) => renderDecoratedSpan(member))
      .join("");
    // 補間値は renderDecoratedSpan() が escapeHtml() 済みテキストから作った断片。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    if (inner.length > 0) html += `<code>${inner}</code>`;
    index = end;
  }
  return html;
}

/** span が属するコード範囲の識別子（コードでなければ null）。 */
function codeGroupOf(span: TextInlineDisplaySpan): number | null {
  if (span.codeRangeIndex !== undefined) return span.codeRangeIndex;
  return span.decoration.code ? DECORATION_ONLY_CODE_GROUP : null;
}

/** 1 つの span を、記法由来の装飾と部分書式の太字だけ反映した HTML へ変換する。 */
function renderDecoratedSpan(span: TextInlineDisplaySpan): string {
  if (span.text.length === 0) return "";
  let html = escapeHtml(span.text);
  // 補間値は escapeHtml() 済みテキストと、この関数が付けた装飾要素だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  if (span.decoration.strikethrough) html = `<del>${html}</del>`;
  // 補間値は escapeHtml() 済みテキストと、この関数が付けた装飾要素だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  if (span.decoration.italic) html = `<em>${html}</em>`;
  // 補間値は escapeHtml() 済みテキストと、この関数が付けた装飾要素だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  if (span.decoration.bold || span.style.bold === true) html = `<strong>${html}</strong>`;
  return html;
}

/**
 * `<a href>` へ入れてよい URL だけを返す（http: / https: / mailto: 以外は null）。
 * 本文のリンク解析（domain 側）が現在返すのは http / https だけで、mailto は許可側に
 * 載せてあるだけの先回りである。ここは許可リストであり、判定の本体は
 * 「javascript: などを通さない」ことにある。
 */
function linkHref(url: string | undefined): string | null {
  if (url === undefined || url.length === 0 || url.length > LINK_URL_MAX_LENGTH) return null;
  if (isHttpUrl(url)) return url;
  try {
    return new URL(url).protocol === "mailto:" ? url : null;
  } catch {
    return null;
  }
}
